import React, { Component } from 'react'
import { Alert, Button, Modal, Spin, Table, Tag, Typography, message } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import {
  approveVibeProjectPermissionRequest,
  getMyVibeProjectPermissionRequestList
} from '../../../request'
import { formatPermissionRequestTime, getPermissionScopeText } from './operationPermissions'

const { Text } = Typography

const STATUS_META = {
  pending: { text: '待授权', color: 'orange' },
  approved: { text: '已同意', color: 'green' },
  expired: { text: '已过期', color: 'default' }
}

/**
 * 待我审批弹窗（从 Profile/VibeProjectList.js 的 renderPermissionRequestModal 抽离）。
 * props：visible / onClose / onChanged（审批成功后的回调，用于刷新待审批数量与详情）
 * 接口：getMyVibeProjectPermissionRequestList、approveVibeProjectPermissionRequest
 */
export default class VibeProjectPermissionRequestDialog extends Component {
  state = {
    loading: false,
    loadError: false,
    requests: [],
    selectedRequestIds: [],
    approvingRequestId: null,
    batchApproving: false
  }

  componentDidMount () {
    if (this.props.visible) this.loadRequests()
  }

  componentDidUpdate (prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.loadRequests()
    } else if (!this.props.visible && prevProps.visible) {
      this.resetState()
    }
  }

  resetState = () => {
    this.setState({
      loading: false,
      loadError: false,
      requests: [],
      selectedRequestIds: [],
      approvingRequestId: null,
      batchApproving: false
    })
  }

  loadRequests = () => {
    this.setState({ loading: true, loadError: false })
    getMyVibeProjectPermissionRequestList({})
      .then(requests => {
        const list = Array.isArray(requests) ? [...requests] : []
        this.setState({
          requests: list,
          selectedRequestIds: list
            .filter(request => request.status === 'pending')
            .map(request => String(request.id))
        })
      })
      .catch(() => this.setState({ loadError: true }))
      .finally(() => this.setState({ loading: false }))
  }

  close = () => {
    if (this.state.batchApproving) return
    if (this.props.onClose) this.props.onClose()
  }

  notifyChanged = () => {
    if (this.props.onChanged) this.props.onChanged()
  }

  approveOne = record => {
    const applicantName = record.applicantUserNick || record.applicantUserName || record.applicantUserId || '申请人'
    const permissionText = getPermissionScopeText(
      record.permissionScope || 'database',
      record.databasePermissionType || 'readwrite'
    )
    Modal.confirm({
      title: `同意 ${applicantName} 的权限申请？`,
      content: `同意后将授予“${record.projectName || '-'}”项目的${permissionText}。`,
      okText: '同意并授权',
      cancelText: '取消',
      onOk: () => {
        this.setState({ approvingRequestId: record.id })
        return approveVibeProjectPermissionRequest({ requestId: record.id })
          .then(() => {
            message.success('已同意并完成授权')
            this.loadRequests()
            this.notifyChanged()
          })
          .finally(() => this.setState({ approvingRequestId: null }))
      }
    })
  }

  approveSelected = async () => {
    const { selectedRequestIds, batchApproving } = this.state
    if (!selectedRequestIds.length || batchApproving) return
    this.setState({ batchApproving: true })
    let successCount = 0
    let failureCount = 0
    // 逐条串行审批，避免并发更新互相覆盖权限。
    for (const requestId of selectedRequestIds) {
      try {
        await approveVibeProjectPermissionRequest({ requestId })
        successCount += 1
      } catch (error) {
        failureCount += 1
      }
    }
    if (!failureCount) {
      message.success(`已完成 ${successCount} 个权限授权`)
    } else if (successCount) {
      message.warning(`已完成 ${successCount} 个授权，${failureCount} 个授权失败`)
    } else {
      message.error('所选权限申请均未授权成功')
    }
    this.setState({ batchApproving: false })
    this.loadRequests()
    this.notifyChanged()
  }

  renderStatus = status => {
    const meta = STATUS_META[(status || '').toLowerCase()] || { text: status || '-', color: 'default' }
    return <Tag color={meta.color}>{meta.text}</Tag>
  }

  render () {
    const { loading, loadError, requests, selectedRequestIds, approvingRequestId, batchApproving } = this.state
    return <Modal
      title="待我审批的权限申请"
      visible={this.props.visible}
      width={1000}
      footer={[
        <Button key="close" onClick={this.close} disabled={batchApproving}>关闭</Button>,
        <Button
          key="batchApprove"
          type="primary"
          icon={<CheckCircleOutlined />}
          loading={batchApproving}
          disabled={!selectedRequestIds.length}
          onClick={this.approveSelected}
        >
          一键授权（{selectedRequestIds.length}）
        </Button>
      ]}
      onCancel={this.close}
      closable={!batchApproving}
      maskClosable={!batchApproving}
      destroyOnClose
    >
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="权限申请加载失败"
          action={<Button size="small" onClick={this.loadRequests}>重试</Button>}
          style={{ marginBottom: 12 }}
        />
      ) : null}
      <Spin spinning={loading}>
        <Table
          rowKey={record => String(record.id)}
          size="small"
          dataSource={requests}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: '暂时没有人申请你的项目' }}
          scroll={{ x: 900 }}
          rowSelection={{
            selectedRowKeys: selectedRequestIds,
            onChange: keys => this.setState({ selectedRequestIds: keys.map(key => String(key)) }),
            getCheckboxProps: record => ({ disabled: record.status !== 'pending' })
          }}
          columns={[
            {
              title: '申请人',
              key: 'applicant',
              width: 180,
              render: (_, record) => (
                <div>
                  <div>{record.applicantUserNick || record.applicantUserName || record.applicantUserId || '-'}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.applicantUserName || record.applicantUserId || '-'}
                  </Text>
                </div>
              )
            },
            {
              title: '申请项目',
              key: 'project',
              width: 200,
              render: (_, record) => (
                <div>
                  <div>{record.projectName || '-'}</div>
                  <Text type="secondary" code style={{ fontSize: 12 }}>{record.projectSlug || '-'}</Text>
                </div>
              )
            },
            {
              title: '申请范围',
              key: 'permissionScope',
              width: 170,
              render: (_, record) => getPermissionScopeText(
                record.permissionScope || 'database',
                record.databasePermissionType || 'readwrite'
              )
            },
            {
              title: '申请时间',
              dataIndex: 'createTime',
              width: 150,
              render: formatPermissionRequestTime
            },
            {
              title: '有效期至',
              dataIndex: 'expireTime',
              width: 150,
              render: formatPermissionRequestTime
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: this.renderStatus
            },
            {
              title: '操作',
              key: 'action',
              fixed: 'right',
              width: 100,
              render: (_, record) => record.status === 'pending' ? (
                <Button
                  type="link"
                  size="small"
                  loading={String(approvingRequestId) === String(record.id)}
                  disabled={batchApproving}
                  onClick={() => this.approveOne(record)}
                >
                  同意
                </Button>
              ) : <Text type="secondary">-</Text>
            }
          ]}
        />
      </Spin>
    </Modal>
  }
}
